import * as yup from 'yup'

const SPECIAL_CHAR_REGEX = /^[a-zA-Z0-9!@#$%^&*|?()\\_`~.'+,/\r\n -]*$/

const convertNewLineStrToStrArray = (str) => {
  if (!str) {
    return []
  }

  return str.split(/\n/g) ?? []
}

const hasUniqueValues = (str, options) => {
  const newOptions = convertNewLineStrToStrArray(str)
  if (!newOptions || !newOptions.length) {
    return true
  }

  // If newOptions has duplicate values, then hasUniqueValues should return false
  // If newOptions has unique values, then hasUniqueValues should return true
  return !newOptions.some((option) => {
    return options.find((opt) => opt.label.trim().toLowerCase() === option.trim().toLowerCase())
  })
}

const validateIsLabelUnique = (value, collectInfo, selectedItem) => {
  if (!collectInfo || !collectInfo.length) {
    return true
  }

  const fieldId = selectedItem?.id ?? null
  return !collectInfo.some((info) => {
    return fieldId !== info.id && info.field.label?.toLowerCase() === value?.toLowerCase()
  })
}

const fieldValidation = {
  type: yup.string().required(),
  label: yup
    .string()
    .required('Label is required')
    .min(3, 'Label should contain at least ${min} characters')
    .max(150, 'Label should contain maximum of ${max} characters')
    .matches(SPECIAL_CHAR_REGEX, 'Please remove restricted characters')
    .test({
      name: 'unique',
      exclusive: true,
      params: {},
      message: 'All labels in a form must be unique',
      test: function (value) {
        const collectInfo = this.options.context?.collectInfo ?? []
        const selectedItem = this.options.context?.selectedItem ?? {}

        return validateIsLabelUnique(value, collectInfo, selectedItem)
      }
    }),
  helpText: yup
    .string()
    .nullable()
    .optional()
    .max(300, 'Help text should contain maximum of ${max} characters')
    .matches(SPECIAL_CHAR_REGEX, 'Please remove restricted characters'),
  linkedTo: yup
    .string()
    .nullable()
    .optional()
    .max(150, 'Link to visitor should contain maximum of ${max} characters')
    .matches(SPECIAL_CHAR_REGEX, 'Please remove restricted characters'),
  options: yup.array().when('type', {
    is: 'dropdown',
    then: yup
      .array()
      .min(1, 'Dropdown should contain at least ${min} value')
      .max(200, 'Dropdown should contain maximum ${max} values')
      .required(),
    otherwise: yup.array().optional()
  }),
  inputOptions: yup
    .string()
    .nullable()
    .test({
      name: 'unique',
      exclusive: true,
      params: {},
      message: 'All options in a form must be unique',
      test: function (value) {
        return hasUniqueValues(value, this.parent.options)
      }
    })
    .matches(SPECIAL_CHAR_REGEX, 'Please remove restricted characters')
    .optional()
}

export const cmeFormFieldValidationSchema = yup
  .object()
  .shape({
    ...fieldValidation
  })
  .optional()

const cmeFormValidationSchema = yup
  .object({
    type: yup.string().optional(),
    action: yup
      .object()
      .shape({
        step: yup.string().required('Step is required')
      })
      .optional(),
    collectInfo: yup
      .array()
      .of(
        yup
          .object()
          .shape({
            field: cmeFormFieldValidationSchema
          })
          .test({
            name: 'unique',
            exclusive: true,
            test: function (value) {
              if (!this.parent || !this.parent.length) {
                return true
              }

              const index = this.parent.indexOf(value)
              const result = this.parent.filter((item) => {
                return item.field.label === value.field.label
              })

              if (result && result.length > 1) {
                return this.createError({
                  path: `collectInfo.${index}.field.label`,
                  message: 'All labels in a form must be unique'
                })
              }

              return true
            }
          })
          .test({
            name: 'allowedTypeCategories',
            exclusive: true,
            test: function (value) {
              const field = value.field
              if (!field || field.type !== 'attachments') {
                return true
              }

              const index = this.parent.indexOf(value)
              const noFileTypeSelected = field.validations.allowedTypeCategories?.length

              if (!noFileTypeSelected) {
                return this.createError({
                  path: `collectInfo.${index}.field.validations.allowedTypeCategories`,
                  message: 'Please select allowed file types'
                })
              }

              return true
            }
          })
          .optional()
      )
      .optional()
  })
  .required()

export default cmeFormValidationSchema
